import { useEffect, useRef, useState } from 'react'

import PropTypes from 'prop-types'

import Icon from 'components/Core/Icon/Icon'
import Table from 'components/Core/Table/Table'

import ItemTableBodyRow from './ItemTableBodyRow'

const ItemTableExpandableBodyRow = ({
  item,
  columns,
  columnOrder,
  className: _className,
  onRowClick: _onRowClick,
  rowClassnames,
  rowsExpanded,
  aggregationKeys,
  onExpandAnyRow,
  ...rest
}) => {
  const [open, setOpen] = useState(new Set())

  const { expandableRow = '', expandedRow = '' } = rowClassnames || {}

  const openKeys = useRef(new Set())

  useEffect(() => {
    rowsExpanded ? setOpen(openKeys.current) : setOpen(new Set())
  }, [rowsExpanded])

  const generateRow = (item, open, setOpen) => {
    const aggregationKey = aggregationKeys.find((key) => !!item[key])
    openKeys.current.add(item[aggregationKey])
    const { subItems: expandableItems = [] } = item

    const handleExpansion = (isOpen) => {
      const set = new Set(open)
      isOpen ? set.delete(item[aggregationKey]) : set.add(item[aggregationKey])
      set.size === 0 ? onExpandAnyRow(false) : onExpandAnyRow(true)
      setOpen(set)
    }

    return (
      <>
        {expandableItems?.length > 0 &&
          renderExpandableRow(
            item,
            handleExpansion,
            open.has(item[aggregationKey]),
          )}
        {expandableItems?.length > 0 && open.has(item[aggregationKey]) && (
          <>
            {expandableItems.map((child) => {
              return generateRow(child, open, setOpen)
            })}
          </>
        )}
        {!expandableItems?.length && <>{renderExpandedRow(item)}</>}
      </>
    )
  }

  const renderExpansionIcon = (isRowExpanded) => {
    const iconName = isRowExpanded ? 'dropup' : 'chevron'
    return <Icon name={iconName} />
  }

  const renderExpandedRow = (item) => {
    return (
      <ItemTableBodyRow
        className={expandedRow}
        isExpandedRow={true}
        selectable={false}
        columnOrder={columnOrder}
        columns={columns}
        item={item}
      />
    )
  }

  const renderExpandableRow = (item, handleExpansion, isOpen) => {
    return (
      <Table.Row
        className={expandableRow}
        onClick={() => handleExpansion(isOpen)}
      >
        <Table.Cell>{renderExpansionIcon(isOpen)}</Table.Cell>
        {columnOrder.map((id) => (
          <Table.Cell
            key={id}
            width={columns[id].width}
            className={
              columns[id].cellClassName && columns[id].cellClassName(item)
            }
            {...columns[id].align}
            {...rest}
          >
            {columns[id].cellContent(item)}
          </Table.Cell>
        ))}
      </Table.Row>
    )
  }

  return <>{generateRow(item, open, setOpen)}</>
}

ItemTableExpandableBodyRow.propTypes = {
  /* item to be used to calculate cell contents */
  item: PropTypes.object.isRequired,
  /*
  The prop columns takes an object containing columnId as key.
  */
  columns: PropTypes.object.isRequired,
  /* array of Column Id in the order that they should be displayed */
  columnOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  onRowClick: PropTypes.func,
  rowsExpanded: PropTypes.bool,
  /* array of properties of object to use for building the different levels */
  aggregationKeys: PropTypes.arrayOf(PropTypes.string),
  onExpandAnyRow: PropTypes.func,
}

ItemTableExpandableBodyRow.defaultProps = {}

export default ItemTableExpandableBodyRow
